import { Link } from "react-router-dom";
import { MessageCircle, Phone, Mail, Youtube, Instagram, MapPin } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import Logo from "./Logo";
import { AREAS, BRAND, NAV_LINKS, waLink } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";

export function Contact() {
  return (
    <section id="contact" className="relative overflow-hidden bg-navy-900/50 py-20 lg:py-28" data-testid="contact-section">
      <div className="spotlight-gold pointer-events-none absolute right-[-10%] top-[-15%] h-[480px] w-[480px]" />
      <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6">
        <SectionHead
          eyebrow="Contact"
          title="Let's Get the Music Started."
          sub={`Speak directly with ${BRAND.contacts.join(" or ")} — we reply personally, usually the same day.`}
        />
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button" className="inline-flex animate-pulse-gold items-center gap-2 rounded-full bg-[#1f9e4d] px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-[#25b558]">
              <MessageCircle className="h-5 w-5" /> WhatsApp Us
            </a>
            <a href={`tel:${BRAND.phone}`} data-testid="contact-call-button" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-light">
              <Phone className="h-5 w-5" /> Call Us · {BRAND.phoneDisplay}
            </a>
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email-button" className="inline-flex items-center gap-2 rounded-full border border-navy-700 px-8 py-4 text-sm font-semibold text-ivory transition-colors hover:border-gold/50 hover:text-gold">
              <Mail className="h-5 w-5" /> Email Us
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-sm text-slate-400">
            <a href={`mailto:${BRAND.email}`} className="text-gold hover:underline" data-testid="contact-email-link">{BRAND.email}</a>
          </p>
          <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-2xl border border-navy-700 bg-navy-850/70 px-6 py-4" data-testid="contact-service-area">
            <MapPin className="h-4 w-4 text-gold" />
            {AREAS.map((a, i) => (
              <span key={a} className="text-sm font-semibold text-slate-300">
                {a}{i < AREAS.length - 1 && <span className="ml-3 text-gold/50">·</span>}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-navy-700 bg-navy-950 pb-24 pt-14 md:pb-10" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 md:grid-cols-3">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
            {BRAND.tagline} Live karaoke entertainment for Chandigarh & the Tricity — retro
            Bollywood and English classics, performed with passion.
          </p>
          <div className="mt-5 flex gap-3">
            <a href={BRAND.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="footer-youtube-link" className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-700 text-slate-300 transition-colors hover:border-gold/50 hover:text-gold">
              <Youtube className="h-4 w-4" />
            </a>
            <a href={BRAND.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram-link" className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-700 text-slate-300 transition-colors hover:border-gold/50 hover:text-gold">
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Explore</p>
          <nav className="mt-4 grid grid-cols-2 gap-2" aria-label="Footer">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={(e) => { e.preventDefault(); scrollToId(l.href); }} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 transition-colors hover:text-gold">
                {l.label}
              </a>
            ))}
            <a href="#book" onClick={(e) => { e.preventDefault(); scrollToId("#book"); }} data-testid="footer-link-book-us" className="text-sm font-semibold text-gold hover:text-gold-light">Book Us</a>
          </nav>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Reach Us</p>
          <div className="mt-4 space-y-2.5 text-sm text-slate-400">
            <a href={`tel:${BRAND.phone}`} className="block transition-colors hover:text-gold" data-testid="footer-phone-link">{BRAND.phoneDisplay} · Call or WhatsApp</a>
            <a href={`mailto:${BRAND.email}`} className="block transition-colors hover:text-gold" data-testid="footer-email-link">{BRAND.email}</a>
            <p className="pt-2 text-xs leading-relaxed text-slate-500">Serving {AREAS.join(" · ")}</p>
          </div>
        </div>
      </div>
      <div className="mx-auto mt-12 flex max-w-7xl flex-col items-center justify-between gap-3 border-t border-navy-800 px-4 pt-6 text-xs text-slate-500 sm:px-6 md:flex-row">
        <p>© {new Date().getFullYear()} Tricity Kroonerz. All rights reserved.</p>
        <div className="flex items-center gap-5">
          <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-gold">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-gold">Terms & Conditions</Link>
          <Link to="/admin" data-testid="footer-admin-link" className="transition-colors hover:text-gold">Admin</Link>
        </div>
      </div>
    </footer>
  );
}

export function MobileBar() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-3 border-t border-gold/20 bg-navy-950/95 backdrop-blur-xl md:hidden" data-testid="mobile-action-bar">
      <a href={waLink()} target="_blank" rel="noreferrer" data-testid="mobile-bar-whatsapp" className="flex items-center justify-center gap-1.5 bg-[#1f9e4d] py-3.5 text-xs font-bold text-white">
        <MessageCircle className="h-4 w-4" /> WhatsApp
      </a>
      <a href={`tel:${BRAND.phone}`} data-testid="mobile-bar-call" className="flex items-center justify-center gap-1.5 py-3.5 text-xs font-bold text-gold">
        <Phone className="h-4 w-4" /> Call
      </a>
      <button onClick={() => scrollToId("#book")} data-testid="mobile-bar-book" className="flex items-center justify-center gap-1.5 bg-gold py-3.5 text-xs font-bold text-navy-950">
        Book Us
      </button>
    </div>
  );
}
